// @givmo/compliance — the organization's registered agents and foreign qualifications (CCM-MOD5B):
// what the section on Filings, a record's panel, the add sheet and the Calendar say of them. Pure
// functions over decoded records; no React, no reads.
//
// ══ WHERE EVERY LINE COMES FROM ════════════════════════════════════════════════════════════════
//
// APPROVED by Jonathan on 2026-10-15 ("go with your recs" at 19:30 PDT; D-CCM-94; the plan folder's
// `scratchpad/2026-10-15-1930-vendor-screens-rulings-log.md`, on REVIEW-41's page, whose text is the
// record): every line below marked D-CCM-94, each named by its frame (V0 to V8).
//
// APPROVED by Jonathan on 2026-10-15 ("go with your recs" at 20:07 PDT; D-CCM-95; the plan folder's
// `scratchpad/2026-10-15-2007-vendor-history-lines-rulings-log.md`): "{label} changed to {new}" for a
// first value recorded later, and "Ended on {date}." where the host can't name who ended a record.
//
// APPROVED by Jonathan on 2026-10-16 ("re:43, approve recs" at 11:29 PDT; D-CCM-97; the plan folder's
// `scratchpad/2026-10-16-1129-record-filing-link-rulings-log.md`, on REVIEW-43's page, whose text is
// the record): every line below marked D-CCM-97, each named by its frame (L1 to L7b, D1) or as one of
// the page's words-only lines, with their "through {Name}’s AI" forms under D-CCM-96.
//
// APPROVED by Jonathan on 2026-10-16 ("yes" at 13:56 PDT; D-CCM-100; the plan folder's
// `scratchpad/2026-10-16-1356-record-filing-link-connector-words-rulings-log.md`): the record side's
// two lines the page doesn't draw, the twins of the filing side's unnamed and AI forms (cut
// 1008-76's design stop, §4.3), marked D-CCM-100 where they are written.
//
// REUSED, approved before and named beside each: "Your date" (D-CCM-21, frame 13), "Added by {name}
// on {date}" with its "Added on {date}" (D-CCM-21 frame 18; copy ruling item 8), D-CCM-22's B2 with
// ruling 9's "Changed on {date}.", and N40's "Its history stays." (D-CCM-51).
//
// ══ THE PRODUCER ═══════════════════════════════════════════════════════════════════════════════
//
// CCM-MOD5A's vendor records, the one store since CCM-MOD5-ONE-STORE: kind, jurisdiction, provider,
// recorded cost, renewal date, source note, whether it is current (ended when not), and each write's
// snapshot. One PUT records, corrects, ends and makes current again, replacing the whole set of facts
// against the revision read. The list's rows also carry the confirmed library filings of each
// record's kind in its jurisdiction (`library_filing_ids`, D-CCM-94 (4)).

import { formatMoneyDisplay } from '@givmo/api-client';
import {
  US_JURISDICTIONS,
  displayComplianceDate,
  displayLocalDate,
  localDayOf,
} from './compliance-decode';
import type { ObligationVendorLink } from './compliance-decode';
import type { NextDate } from './compliance-filings-model';
import { addedByLine } from './compliance-panel-model';
import { throughTheirAi } from './review-words';

// ── The words ────────────────────────────────────────────────────────────────────────────────────

/** D-CCM-94 (V0): the section's title on Filings; also the record panel's eyebrow (V2, V4, V5). */
export const RECORDS_SECTION_TITLE = 'Registered agents and foreign qualifications';
/** D-CCM-94 (V0): under the title. True of the producer: a reminder goes only for a dated record. */
export const RECORDS_SECTION_LINE =
  'Your organization’s own records. Givmo emails a reminder before each renewal date you record.';
/** D-CCM-94 (V0): the section's button, for someone who can make changes. */
export const ADD_A_RECORD = 'Add a record';
/**
 * D-CCM-97 (D1): the first visit's third way in, in its peers' first person ("Add what I already
 * know", "Upload what I have"). Its line is V0's section line and its button V0's, opening V3's sheet.
 */
export const RECORDS_DOOR_TITLE = 'Add my registered agents and foreign qualifications';
/** D-CCM-94 (V0): the empty section. */
export const NO_RECORDS = 'No registered agents or foreign qualifications recorded yet.';
/** D-CCM-94 (V1): the table's columns, on the families' grid. */
export const RECORD_COLUMNS = {
  record: 'Record',
  cost: 'Recorded cost',
  provider: 'Provider',
  renewal: 'Renewal date',
} as const;
/** D-CCM-94 (V1): the two kinds, under each jurisdiction's name. */
export const RECORD_KIND_NAMES = {
  registered_agent: 'Registered agent',
  foreign_qualification: 'Foreign qualification',
} as const;
/** REUSED (D-CCM-21, frame 13): Filings' line under an organization's own date. */
export const YOUR_DATE = 'Your date';
/** D-CCM-94 (V1), from the panel's "Show {n} earlier years": the ended records, folded. */
export function showEndedRecords(count: number): string {
  return `Show ${count} ended ${count === 1 ? 'record' : 'records'}`;
}
/** D-CCM-94 (V1, V5): an ended record's date cell, and its panel's date block. */
export const ENDED = 'Ended';
/** D-CCM-94 (V2): the date block's title; also the date field's label (V3, V4). */
export const RENEWAL_DATE = 'Renewal date';
/** D-CCM-94 (V2), from the own filing's "Each year on the date you set · Change" (frame 20). */
export const THE_DATE_YOU_RECORDED = 'The date you recorded';
/**
 * D-CCM-94 (V2, V3, V4): true of the producer's rungs, 45, 15 and 3 days before and on the day
 * itself, and never after it (the backend's `COMPLIANCE_VENDOR_LEAD_DAYS`, cut 1008-70).
 */
export const REMINDER_LINE = 'Givmo emails a reminder 45, 15 and 3 days before, and on the day.';
/** D-CCM-94 (V2, words only): an undated record's date block, with "Add". */
export const NO_DATE_RECORDED = 'No date recorded';
export const NO_DATE_LINE = 'Without a date, Givmo sends no reminder.';
/** D-CCM-94 (V2, and the reminder email's V7), from frame 20's own-filing line (D-CCM-21). */
export const OWN_RECORD_LINE =
  'This is your organization’s own record. Givmo hasn’t checked it against a source.';
/** D-CCM-94 (V2, V3): the record's facts, each a panel section and an add-sheet field. */
export const PROVIDER = 'Provider';
export const RECORDED_COST = 'Recorded cost';
export const SOURCE_NOTE = 'Source note';
/** D-CCM-94 (V2): the panel menu's act, from S1 "Stop tracking it" (D-CCM-58). */
export const END_THIS_RECORD = 'End this record';
/** D-CCM-94 (V3): the add sheet's title, the type's label and its button. */
export const ADD_RECORD_TITLE = 'Add a registered agent or foreign qualification';
export const RECORD_TYPE = 'Type';
export const ADD_RECORD = 'Add record';
/** D-CCM-94 (V3b): where a current record of the same type and jurisdiction already exists. */
export const OPEN_IT = 'Open it';
/** D-CCM-94 (V4), from the built "Correct the number". */
export const CHANGE_THE_RENEWAL_DATE = 'Change the renewal date';
/** D-CCM-94 (V5), from S2 "Stop tracking {filing}?" (D-CCM-58). */
export function endRecordTitle(record: string): string {
  return `End ${record}?`;
}
/** N40's first sentence (D-CCM-51, REUSED), then D-CCM-94's (V5): the dialog and the ended block. */
export const END_RECORD_LINE = 'Its history stays. Givmo sends no more reminders for it.';
/** D-CCM-94 (V5), from S1 "Stop tracking it" and "Keep tracking it". */
export const END_IT = 'End it';
export const KEEP_IT = 'Keep it';
/** D-CCM-94 (V5), from S4 "Start tracking it again": it acts at once. */
export const CURRENT_AGAIN = 'It’s current again';
/** D-CCM-94 (V5): the changes list's entries for an ending and its undoing. */
export const RECORD_ENDED = 'Record ended';
export const RECORD_CURRENT_AGAIN = 'Record current again';
/** D-CCM-94 (V6): the Calendar row's basis, from "Each year on the date you set". */
export const RENEWAL_DATE_YOU_RECORDED = 'Renewal date you recorded';

/** D-CCM-94 (V5), from S3: who ended the record, and when. */
export function endedBy(name: string, day: string): string {
  return `Ended by ${name} on ${day}.`;
}
/** D-CCM-95: the same, where the host can't name who ended it. */
export function endedOn(day: string): string {
  return `Ended on ${day}.`;
}

/** A sentence with a link in it: the words before the link, the link's own words, and after. */
export interface LinkedLine {
  before: string;
  link: string;
  after: string;
}

/** D-CCM-94 (V3b): "Your records already include {record}." The record is a link ("Open it"). */
export function alreadyIncludesLine(record: string): string {
  return `Your records already include ${record}.`;
}

/** D-CCM-94 (V8a): "Your filings also include {filing}." The filing's title opens its panel. */
export function filingsAlsoIncludeLine(filing: string): LinkedLine {
  return { before: 'Your filings also include ', link: filing, after: '.' };
}

/**
 * D-CCM-94 (V8b, V6): "Your records also include {record}, renewing {date}.", with ", renewing
 * {date}" only where the record has a date. The record's title opens its panel.
 */
export function recordsAlsoIncludeLine(record: VendorRecord): LinkedLine {
  return {
    before: 'Your records also include ',
    link: vendorTitle(record),
    after:
      record.renewalOn !== undefined
        ? `, renewing ${displayComplianceDate(record.renewalOn)}.`
        : '.',
  };
}

// ── The records, as the producer serves them ─────────────────────────────────────────────────────

export type VendorKind = keyof typeof RECORD_KIND_NAMES;

export interface VendorRecord {
  vendorId: string;
  kind: VendorKind;
  jurisdictionCode: string;
  providerName?: string;
  /** As served: a decimal string. Money is never parsed. */
  recordedCostUsd?: string;
  /** The recorded renewal date, a civil `YYYY-MM-DD`. */
  renewalOn?: string;
  sourceNote?: string;
  isCurrent: boolean;
  revision: number;
  recordedByUserId?: string;
  recordedAt?: string;
  /** The confirmed library filings of its kind in its jurisdiction, by obligation id (the list's). */
  libraryFilingIds: string[];
  /** CCM-MOD5-RECORD-FILING-LINK: its open link to the library filing it is, where one stands. */
  filingLink?: VendorFilingLink;
}

/**
 * One link between a record and the library filing it is (D-CCM-97), as the record's reads serve
 * it: open on the list's row (`filing_link`), every one it has had on its detail (`filing_links`).
 */
export interface VendorFilingLink {
  linkId: string;
  obligationId: string;
  linkedByUserId?: string;
  linkedAt?: string;
  /** `web` (a person in Givmo) or `connector` (that person's connected AI). */
  linkedChannel?: string;
  unlinkedByUserId?: string;
  unlinkedAt?: string;
  unlinkedChannel?: string;
}

function decodeFilingLink(value: unknown): VendorFilingLink | undefined {
  if (!isRecord(value)) return undefined;
  const linkId = text(value.link_id);
  const obligationId = text(value.obligation_id);
  if (linkId === undefined || obligationId === undefined) return undefined;
  return {
    linkId,
    obligationId,
    linkedByUserId: text(value.linked_by_user_id),
    linkedAt: text(value.linked_at),
    linkedChannel: text(value.linked_channel),
    unlinkedByUserId: text(value.unlinked_by_user_id),
    unlinkedAt: text(value.unlinked_at),
    unlinkedChannel: text(value.unlinked_channel),
  };
}

export interface VendorEvent {
  eventId: string;
  revision: number;
  actorUserId?: string;
  occurredAt?: string;
  snapshot: VendorRecord;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value : undefined;

/** A served cost as its decimal string: a number arrives only from an older or odd writer. */
function costText(value: unknown): string | undefined {
  if (typeof value === 'string') return text(value);
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

/** One served record, or `undefined` where it can't be read as one (no id, or a kind this build doesn't know). */
export function decodeVendorRecord(row: unknown): VendorRecord | undefined {
  if (!isRecord(row)) return undefined;
  const vendorId = text(row.vendor_id);
  const kind = row.record_kind;
  if (vendorId === undefined || (kind !== 'registered_agent' && kind !== 'foreign_qualification')) {
    return undefined;
  }
  const filings = Array.isArray(row.library_filing_ids) ? row.library_filing_ids : [];
  return {
    vendorId,
    kind,
    jurisdictionCode: typeof row.jurisdiction_code === 'string' ? row.jurisdiction_code : '',
    providerName: text(row.provider_name),
    recordedCostUsd: costText(row.recorded_cost_usd),
    renewalOn: text(row.renewal_on),
    sourceNote: text(row.source_note),
    // Only an explicit `false` ends a record: the producer's default is current.
    isCurrent: row.is_current !== false,
    revision: typeof row.revision === 'number' ? row.revision : 0,
    recordedByUserId: text(row.recorded_by_user_id),
    recordedAt: text(row.recorded_at),
    libraryFilingIds: filings.filter((id): id is string => typeof id === 'string' && id !== ''),
    filingLink: decodeFilingLink(row.filing_link),
  };
}

/** Every readable record of a list read's rows. */
export function decodeVendorRecords(rows: readonly unknown[]): VendorRecord[] {
  return rows
    .map(decodeVendorRecord)
    .filter((record): record is VendorRecord => record !== undefined);
}

/**
 * One record's detail: the record and each write's snapshot, oldest first, and every link it has
 * had (`filing_links`, oldest first), its open one beside the record (`filing_link`).
 */
export function decodeVendorDetail(body: unknown): {
  vendor?: VendorRecord;
  events: VendorEvent[];
  filingLinks: VendorFilingLink[];
} {
  if (!isRecord(body)) return { events: [], filingLinks: [] };
  const filingLinks = (Array.isArray(body.filing_links) ? body.filing_links : [])
    .map(decodeFilingLink)
    .filter((link): link is VendorFilingLink => link !== undefined);
  const decoded = decodeVendorRecord(body.vendor);
  const open = decodeFilingLink(body.filing_link);
  const vendor =
    decoded !== undefined && open !== undefined ? { ...decoded, filingLink: open } : decoded;
  const events = (Array.isArray(body.events) ? body.events : [])
    .filter(isRecord)
    .flatMap((row): VendorEvent[] => {
      const snapshot = decodeVendorRecord(row.snapshot);
      if (snapshot === undefined || typeof row.revision !== 'number') return [];
      return [
        {
          eventId: text(row.event_id) ?? `${snapshot.vendorId}-${row.revision}`,
          revision: row.revision,
          actorUserId: text(row.actor_user_id),
          occurredAt: text(row.occurred_at),
          snapshot,
        },
      ];
    })
    .sort((a, b) => a.revision - b.revision);
  return vendor !== undefined ? { vendor, events, filingLinks } : { events, filingLinks };
}

// ── Naming a record ──────────────────────────────────────────────────────────────────────────────

/**
 * The jurisdiction's name. The wire carries only the code, so the web's own list names it. A code
 * the list doesn't hold stands for itself ("PR") wherever a screen names the record: its row, its
 * panel's title and its Calendar date, as the reminder email's subject and first line name it too.
 * The add sheet never offers such a code, but the record's PUT (on either door) and the connector
 * take any two capital letters.
 */
export function jurisdictionName(code: string): string {
  return US_JURISDICTIONS.find((each) => each.code === code)?.name ?? code;
}

/** "Maryland · Registered agent", as every Compliance screen names a filing. */
export function vendorTitle(record: VendorRecord): string {
  return `${jurisdictionName(record.jurisdictionCode)} · ${RECORD_KIND_NAMES[record.kind]}`;
}

/** "$125.00": the organization's own figure as it recorded it (J3), read from its decimal string. */
export function displayRecordedCost(cost: string | undefined): string | undefined {
  if (cost === undefined) return undefined;
  const shown = formatMoneyDisplay(cost);
  return shown !== null ? `$${shown}` : undefined;
}

/** A recorded cost as the producer takes it: dollars, with at most two places for cents. */
const COST_PATTERN = /^\d{1,11}(\.\d{1,2})?$/;

/** Dollars grouped by thousands, as a person writes them: "1,250", "12,500,000.00". */
const GROUPED_COST_PATTERN = /^[1-9]\d{0,2}(,\d{3})+(\.\d{1,2})?$/;

/**
 * A cost as the organization typed it, read as the producer takes it, or `undefined` where it can't
 * be read, which holds the write. A leading "$" and thousands separators are only how a person
 * writes an amount, so "$125" is 125 and "1,250.00" is 1250.00. A comma anywhere else is never read
 * away: "12,5" may be a decimal comma, and stays unread rather than becoming 125.
 */
export function parseRecordedCost(typed: string): string | undefined {
  const amount = typed.trim().replace(/^\$\s*/, '');
  const plain = GROUPED_COST_PATTERN.test(amount) ? amount.replace(/,/g, '') : amount;
  return COST_PATTERN.test(plain) ? plain : undefined;
}

// ── The section on Filings (V0, V1) ──────────────────────────────────────────────────────────────

/**
 * V1's order: current records by renewal date, soonest first, then those with no date, each tie by
 * jurisdiction and type; the ended records after, folded, in the same order. (Maryland's Oct 1, D.C.'s
 * Apr 1, then Delaware's with none.)
 */
export function sectionRecords(records: readonly VendorRecord[]): {
  current: VendorRecord[];
  ended: VendorRecord[];
} {
  const byDate = (a: VendorRecord, b: VendorRecord) => {
    if (a.renewalOn !== b.renewalOn) {
      if (a.renewalOn === undefined) return 1;
      if (b.renewalOn === undefined) return -1;
      return a.renewalOn.localeCompare(b.renewalOn);
    }
    return vendorTitle(a).localeCompare(vendorTitle(b));
  };
  return {
    current: records.filter((record) => record.isCurrent).sort(byDate),
    ended: records.filter((record) => !record.isCurrent).sort(byDate),
  };
}

/**
 * V3b: a current record of the type and jurisdiction the add sheet has chosen, which the sheet
 * names and links. The producer allows a second one, so the sheet never refuses it.
 */
export function currentRecordOf(
  records: readonly VendorRecord[],
  kind: string,
  jurisdictionCode: string,
): VendorRecord | undefined {
  return sectionRecords(records).current.find(
    (record) => record.kind === kind && record.jurisdictionCode === jurisdictionCode,
  );
}

// ── A record's history (V2, V5; D-CCM-22's B2, D-CCM-95) ─────────────────────────────────────────

export interface VendorHistoryEntry {
  key: string;
  /** What changed. */
  what: string;
  /** Who and when. */
  how: string;
  /** The act's instant, which orders the list. */
  at: string;
}

type Fact = 'renewal' | 'provider' | 'cost' | 'note';

const FACT_LABELS: Record<Fact, string> = {
  renewal: RENEWAL_DATE,
  provider: PROVIDER,
  cost: RECORDED_COST,
  note: SOURCE_NOTE,
};

/** Within one write, the facts in the order the panel shows them, newest-first reversed below. */
const FACT_ORDER: readonly Fact[] = ['renewal', 'provider', 'cost', 'note'];

function factWords(fact: Fact, record: VendorRecord): string | undefined {
  switch (fact) {
    case 'renewal':
      return record.renewalOn !== undefined ? displayComplianceDate(record.renewalOn) : undefined;
    case 'provider':
      return record.providerName;
    case 'cost':
      return displayRecordedCost(record.recordedCostUsd);
    case 'note':
      return record.sourceNote;
  }
}

/** D-CCM-22's B2 second line, or ruling 9's form for a person the host can't name. */
function changedLine(name: string | undefined, day: string): string {
  return name !== undefined ? `Changed by ${name} on ${day}.` : `Changed on ${day}.`;
}

/**
 * The record's "Corrections and changes", newest first: each write's changes against the write
 * before it. The first write is the record's making, which "Added by" says, so it lists nothing.
 *
 * - A fact moved: "{label} changed from {old} to {new}" (B2), or, recorded for the first time,
 *   "{label} changed to {new}" (D-CCM-95). A fact cleared has no approved words, and is left out.
 * - Ended: "Record ended" over "Ended by {name} on {date}." ("Ended on {date}." unnamed, D-CCM-95).
 * - Current again: "Record current again" over B2's "Changed by …" (ruling 9's form unnamed).
 * - An act that can't be dated is left out rather than shown with a gap.
 */
export function vendorHistory(
  events: readonly VendorEvent[],
  nameOf: (userId: string) => string | undefined,
  timeZone?: string,
): VendorHistoryEntry[] {
  const ordered = [...events].sort((a, b) => a.revision - b.revision);
  const entries: VendorHistoryEntry[] = [];
  for (let index = 1; index < ordered.length; index += 1) {
    const before = ordered[index - 1]!.snapshot;
    const event = ordered[index]!;
    const after = event.snapshot;
    const day = displayLocalDate(event.occurredAt, timeZone);
    if (day === '—' || event.occurredAt === undefined) continue;
    const name = personNamed(event.actorUserId, nameOf);
    const at = event.occurredAt;
    const made: VendorHistoryEntry[] = [];
    for (const fact of FACT_ORDER) {
      const from = factWords(fact, before);
      const to = factWords(fact, after);
      if (to === undefined || from === to) continue;
      made.push({
        key: `${event.eventId}:${fact}`,
        what:
          from !== undefined
            ? `${FACT_LABELS[fact]} changed from ${from} to ${to}`
            : `${FACT_LABELS[fact]} changed to ${to}`,
        how: changedLine(name, day),
        at,
      });
    }
    if (before.isCurrent !== after.isCurrent) {
      made.push({
        key: `${event.eventId}:current`,
        what: after.isCurrent ? RECORD_CURRENT_AGAIN : RECORD_ENDED,
        how: after.isCurrent
          ? changedLine(name, day)
          : name !== undefined
            ? endedBy(name, day)
            : endedOn(day),
        at,
      });
    }
    entries.push(...made);
  }
  // Newest first; within one write, its entries in reverse of the panel's fact order.
  return entries.reverse();
}

function personNamed(
  userId: string | undefined,
  nameOf: (userId: string) => string | undefined,
): string | undefined {
  if (userId === undefined) return undefined;
  const name = nameOf(userId)?.trim();
  return name !== undefined && name.length > 0 ? name : undefined;
}

/**
 * "Added by {name} on {date}", or "Added on {date}" for someone the host can't name (the year
 * tiles' line and its form, D-CCM-21 frame 18 and copy ruling item 8): the record's first write.
 */
export function vendorAddedLine(
  record: VendorRecord,
  events: readonly VendorEvent[],
  nameOf: (userId: string) => string | undefined,
  timeZone?: string,
): string | undefined {
  const first = [...events].sort((a, b) => a.revision - b.revision)[0];
  const day = localDayOf(first?.occurredAt ?? record.recordedAt, timeZone);
  if (day === undefined) return undefined;
  const by = first?.actorUserId ?? record.recordedByUserId;
  return addedByLine({ addedBy: by !== undefined ? [by] : [], addedOn: [day] }, nameOf);
}

/**
 * The ended block's line (V5b): "Ended by {name} on {date}." from the latest write that ended the
 * record, or "Ended on {date}." where the host can't name who (D-CCM-95).
 */
export function endedLine(
  events: readonly VendorEvent[],
  nameOf: (userId: string) => string | undefined,
  timeZone?: string,
): string | undefined {
  const event = endingEvent(events);
  const day = displayLocalDate(event?.occurredAt, timeZone);
  if (event?.occurredAt === undefined || day === '—') return undefined;
  const name = personNamed(event.actorUserId, nameOf);
  return name !== undefined ? endedBy(name, day) : endedOn(day);
}

/** The latest write that ended the record, if one did. */
function endingEvent(events: readonly VendorEvent[]): VendorEvent | undefined {
  const ordered = [...events].sort((a, b) => a.revision - b.revision);
  for (let index = ordered.length - 1; index > 0; index -= 1) {
    const event = ordered[index]!;
    if (!event.snapshot.isCurrent && ordered[index - 1]!.snapshot.isCurrent) return event;
  }
  return undefined;
}

/** The day the record ended, as the ended block's line says it, if it can be read. */
export function endedDay(events: readonly VendorEvent[], timeZone?: string): string | undefined {
  const day = displayLocalDate(endingEvent(events)?.occurredAt, timeZone);
  return day !== '—' ? day : undefined;
}

// ── The Calendar (V6) ────────────────────────────────────────────────────────────────────────────

export interface VendorCalendarDate {
  key: string;
  record: VendorRecord;
  dueOn: string;
  dateLabel: string;
  title: string;
  basis: string;
  /** The provider's name, where one is recorded: the row's quiet line. */
  lines: string[];
  /** L5: the library filing the record is linked to, whose row this is, titled by it. */
  filingId?: string;
}

/**
 * Each current record with a renewal date, as the Calendar's own dates (J2). A date that has
 * passed stays listed, as an own filing's does: the Calendar never drops a recorded date.
 */
export function vendorCalendarDates(records: readonly VendorRecord[]): VendorCalendarDate[] {
  return records
    .filter((record) => record.isCurrent && record.renewalOn !== undefined)
    .map((record) => ({
      key: `vendor-${record.vendorId}`,
      record,
      dueOn: record.renewalOn as string,
      dateLabel: displayComplianceDate(record.renewalOn),
      title: vendorTitle(record),
      basis: RENEWAL_DATE_YOU_RECORDED,
      lines: record.providerName !== undefined ? [record.providerName] : [],
    }));
}

// ── The cross-reference (V8; D-CCM-94 (4)) ───────────────────────────────────────────────────────

/**
 * The current records that name a library filing: each record whose list row names it. A filing
 * linked to a record names none (L3: its linked line stands in V8b's place).
 */
export function recordsNaming(
  records: readonly VendorRecord[],
  obligationId: string,
): VendorRecord[] {
  if (records.some((record) => record.filingLink?.obligationId === obligationId)) return [];
  return sectionRecords(records).current.filter((record) =>
    record.libraryFilingIds.includes(obligationId),
  );
}

// ── A record linked to the library filing it is (D-CCM-97) ──────────────────────────────────────
//
// CCM-MOD5-RECORD-FILING-LINK. A link is the organization's own statement that one of its records
// IS a filing from Givmo's library: the filing then shows the record's date as its own, where Givmo
// has none, and the Calendar lists it once. Every line says who made it; none says the organization
// is qualified, current or registered anywhere.

/** D-CCM-97 (L1, L7a): the act after V8a's and V8b's lines. It acts at once (choice 1). */
export const LINK_THEM = 'Link them';
/** D-CCM-97 (L4): undoing it, after the record's linked line. It acts at once (choice 3). */
export const UNLINK = 'Unlink';
/**
 * D-CCM-97 (L3m): the same act in the filing's ⋯ menu, after "Copy link", named, since "link" alone
 * there reads as the page's address.
 */
export const UNLINK_YOUR_RECORD = 'Unlink your record';

/** Who made one link act, and when: a person in Givmo, their connected AI, or someone unnamed. */
export interface LinkAct {
  /** The roster's name for the person, if it has one. */
  name?: string;
  /** `connector` for their connected AI; anything else reads as the person in Givmo. */
  channel?: string;
  day: string;
}

/**
 * D-CCM-97 (L3, L7b), from S3 "Tracking stopped by {Name} on {date}.": the filing's line naming its
 * record, in V8b's place. "Your record" says which side the title is (F3). Its words-only form for
 * someone the host can't name, "… on {date}.", and N56's "through {Name}’s AI" (D-CCM-96).
 */
export function linkedToRecordLine(record: string, act: LinkAct): LinkedLine {
  return { before: 'Linked to your record ', link: record, after: linkedAfter(act) };
}

/**
 * D-CCM-97 (L4): the record's line naming its filing, in V8a's place. Its two other forms, the
 * filing side's twins, are D-CCM-100's: "Linked to your filing {filing} on {date}." where the host
 * can't name the person, and "Linked to your filing {filing} through {Name}’s AI on {date}." for a
 * link the person's connected AI made.
 */
export function linkedToFilingLine(filing: string, act: LinkAct): LinkedLine {
  return { before: 'Linked to your filing ', link: filing, after: linkedAfter(act) };
}

function linkedAfter(act: LinkAct): string {
  if (act.channel === 'connector') return ` through ${throughTheirAi(act.name)} on ${act.day}.`;
  return act.name !== undefined ? ` by ${act.name} on ${act.day}.` : ` on ${act.day}.`;
}

/**
 * D-CCM-97 (words only): the filing while its linked record is ended, under "Givmo doesn’t have a
 * date for this yet." (REUSED), back in "Not supported yet".
 */
export function linkedRecordEndedLine(record: string, endedOn: string): LinkedLine {
  return { before: 'Linked to your record ', link: record, after: `, which ended on ${endedOn}.` };
}

/** D-CCM-97 (L3, L4 and the words-only lines): each side's "Corrections and changes" entries. */
export function linkedEntry(side: 'record' | 'filing', other: string): string {
  return `Linked to your ${side} ${other}`;
}
export function unlinkedEntry(side: 'record' | 'filing', other: string): string {
  return `Unlinked from your ${side} ${other}`;
}

/**
 * D-CCM-97: an entry's second line, in B2's form: "Linked by {Name} on {date}.", "Linked on
 * {date}." where the host can't name the person, and N56's "Linked through {Name}’s AI on {date}."
 * (D-CCM-96); the same for "Unlinked".
 */
export function linkActLine(verb: 'Linked' | 'Unlinked', act: LinkAct): string {
  if (act.channel === 'connector')
    return `${verb} through ${throughTheirAi(act.name)} on ${act.day}.`;
  return act.name !== undefined
    ? `${verb} by ${act.name} on ${act.day}.`
    : `${verb} on ${act.day}.`;
}

/** D-CCM-97 (choice 9): V5's end dialog, for a linked record with a date, after its line. */
export function wontShowWhileEndedLine(filing: string): string {
  return `${filing} won’t show this record’s date while it’s ended.`;
}

/** D-CCM-97 (choice 9): S2's stop dialog, for a filing whose linked record is current and dated. */
export function stillRemindsLine(record: string): string {
  return `Givmo still emails reminders for your record ${record}.`;
}

/** The linked record's title, from what the filing's read serves of it ("District of Columbia · Foreign qualification"). */
export function linkedRecordTitle(link: ObligationVendorLink): string {
  const kind = RECORD_KIND_NAMES[link.recordKind as VendorKind] ?? link.recordKind;
  return `${jurisdictionName(link.jurisdictionCode)} · ${kind}`;
}

/**
 * What a library filing's link gives it (D-CCM-97 (2) and (9); the design stop's 4.5): the record's
 * date, filling a filing Givmo has no date for while it is tracked and the record current and
 * dated; else the reason it shows none. Never a date over Givmo's own (fill, never override).
 */
export type LinkedDate =
  | { kind: 'dated'; dueOn: string }
  | { kind: 'undated' }
  | { kind: 'ended' }
  | { kind: 'none' };

export function linkedDate(
  link: ObligationVendorLink | undefined,
  givmoHasNoDate: boolean,
  trackingStopped: boolean,
): LinkedDate {
  if (link === undefined || !givmoHasNoDate || trackingStopped) return { kind: 'none' };
  if (!link.isCurrent) return { kind: 'ended' };
  return link.renewalOn !== undefined
    ? { kind: 'dated', dueOn: link.renewalOn }
    : { kind: 'undated' };
}

/**
 * L5's date-once rule over the Calendar's own lists: a linked filing whose record's date fills it
 * leaves "Not supported yet" and takes the record's row, in V6's form titled by the filing, whose
 * title opens the filing; the record's own row isn't listed. Every other record row stays, a
 * stopped filing's record and a filing Givmo dates included (D-CCM-97 (2) and (9)). The filing
 * moves only onto a row that is there: the records come from a read of their own, so until it
 * lands, or if it fails, the filing stays in "Not supported yet" with its linked line.
 */
export function linkedCalendarRows<G extends { ref: { kind: string; id: string } }>(
  gaps: readonly G[],
  recordDates: readonly VendorCalendarDate[],
  linkOf: (obligationId: string) => ObligationVendorLink | undefined,
  titleOf: (gap: G) => string,
): { gaps: G[]; recordDates: VendorCalendarDate[] } {
  const listed = new Set(recordDates.map((date) => date.record.vendorId));
  const filled = new Map<string, G>();
  for (const gap of gaps) {
    if (gap.ref.kind !== 'library') continue;
    const link = linkOf(gap.ref.id);
    if (
      link !== undefined &&
      linkedDate(link, true, false).kind === 'dated' &&
      listed.has(link.vendorId)
    ) {
      filled.set(link.vendorId, gap);
    }
  }
  return {
    gaps: gaps.filter((gap) => ![...filled.values()].includes(gap)),
    recordDates: recordDates.map((date) => {
      const gap = filled.get(date.record.vendorId);
      return gap !== undefined ? { ...date, title: titleOf(gap), filingId: gap.ref.id } : date;
    }),
  };
}

/**
 * A record's links in its "Corrections and changes": a link, and its unlink where it was closed,
 * each over who and when. The filing's title is the page's own; a link whose filing or day can't
 * be read is left out, as the model's rule says.
 */
export function vendorLinkHistory(
  links: readonly VendorFilingLink[],
  filingTitle: (obligationId: string) => string | undefined,
  nameOf: (userId: string) => string | undefined,
  timeZone?: string,
): VendorHistoryEntry[] {
  const entries: VendorHistoryEntry[] = [];
  for (const link of links) {
    const title = filingTitle(link.obligationId);
    if (title === undefined) continue;
    const acts: [
      string | undefined,
      string | undefined,
      string | undefined,
      'Linked' | 'Unlinked',
    ][] = [
      [link.linkedAt, link.linkedByUserId, link.linkedChannel, 'Linked'],
      [link.unlinkedAt, link.unlinkedByUserId, link.unlinkedChannel, 'Unlinked'],
    ];
    for (const [at, by, channel, verb] of acts) {
      const day = displayLocalDate(at, timeZone);
      if (at === undefined || day === '—') continue;
      const name = personNamed(by, nameOf);
      entries.push({
        key: `${link.linkId}:${verb}`,
        what: verb === 'Linked' ? linkedEntry('filing', title) : unlinkedEntry('filing', title),
        how: linkActLine(verb, {
          day,
          ...(name !== undefined ? { name } : {}),
          ...(channel !== undefined ? { channel } : {}),
        }),
        at,
      });
    }
  }
  return entries;
}

/**
 * L6 (D-CCM-97): on Filings, a library filing its linked record's date fills reads that date in
 * the own-date form ("Apr 1, 2027 · Your date", frame 13), where it read "Not supported yet"; the
 * group's "Next" then counts it. Every other row is as composed.
 */
export function withLinkedDate<R extends { ref: { kind: string; id: string }; next: NextDate }>(
  row: R,
  linkOf: (obligationId: string) => ObligationVendorLink | undefined,
): R {
  if (row.ref.kind !== 'library' || row.next.kind !== 'not-supported') return row;
  const filled = linkedDate(linkOf(row.ref.id), true, false);
  return filled.kind === 'dated'
    ? { ...row, next: { kind: 'dated', dueOn: filled.dueOn, own: true } }
    : row;
}
